<template>
  <div>
    <div class="d-flex justify-content-end">
      <div class="d-flex justify-content-between button-group">
        <img v-if="currentRoutePermissions.add_salescontract"
             src="@/assets/images/common-icons/icon_create.svg"
             @click="create"
             :title="'create sales contract'">
        <img v-if="currentRoutePermissions.delete_salescontract"
             src="@/assets/images/common-icons/icon_delete.svg"
             @click="deleteSalesContracts"
             :title="'delete sales contracts'">
        <img src="@/assets/images/common-icons/icon_clear.png"
             @click="cleanFilters"
             :title="$t('buttons.common.clear_filters')">
        <img src="@/assets/images/common-icons/icon_search.svg"
             @click="search"
             :title="$t('buttons.common.search')">
      </div>
    </div>
    <div class="search-form">
      <div class="search-form__filters">
        <b-row>
          <b-col>
            <label>{{$t('labels.sale_contract.contract_end_date')}}</label>
            <range-date-picker v-model="searchData.rangeDate" range lang="en"
                               range-separator="/" :first-day-of-week="1"
                               placeholder="Select date of contract period"></range-date-picker>
          </b-col>
          <b-col>
            <label>{{$t('labels.common.status')}}</label>
            <multiselect v-model="searchData.statuses" :max-height="200"
                         placeholder="Choose contract statuses" label="text" track-by="text"
                         :options="statuses" :multiple="true"
                         :close-on-select="false"></multiselect>
          </b-col>
          <b-col>
            <label>{{$t('labels.common.buyer')}}</label>
            <multiselect v-model="searchData.buyers" :max-height="200"
                         placeholder="Choose buyers" label="name" track-by="name"
                         :options="buyers" :multiple="true"
                         :close-on-select="false"></multiselect>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <label>{{$t('labels.sale_contract.contract_nr')}}</label>
            <input class="form-control" placeholder="Enter contract number"
                   v-model="searchData.contract_nr">
          </b-col>
          <b-col>
            <label>{{$t('labels.common.creator')}}</label>
            <multiselect v-model="searchData.creators" :max-height="200"
                         placeholder="Choose creators" label="username" track-by="username"
                         :options="creators" :multiple="true"
                         :close-on-select="false"></multiselect>
          </b-col>
          <b-col>
            <label>{{$t('labels.common.seller')}}</label>
            <multiselect v-model="searchData.sellers" :max-height="200"
                         placeholder="Choose sellers" label="name" track-by="name"
                         :options="sellers" :multiple="true"
                         :close-on-select="false"></multiselect>
          </b-col>
        </b-row>
        <b-row>
          <b-col offset="8">
            <label>{{$t('labels.common.delivery_method')}}</label>
            <multiselect v-model="searchData.delivery_methods" :max-height="200"
                         placeholder="Choose delivery methods" label="name" track-by="name"
                         :options="delivery_methods" :multiple="true"
                         :close-on-select="false"></multiselect>
          </b-col>
        </b-row>
      </div>
      <div>
        <b-row>
          <b-col cols="4">
            <input class="form-control"
                   placeholder="Filter..."
                   @input="setQuickFilter">
          </b-col>
          <b-col cols="4" offset="4" class="vertical-center-container d-flex justify-content-end">
            <label for="pagination-toggle" class="margin-right-5">Pagination</label>
            <input id="pagination-toggle" type="checkbox"
                   title="this checkbox needed for toggle pagination bar" v-model="paginationState"
                   @change="togglePagination">
            <save-columns-btn :grid="gridOptions" :grid-name="gridName"
                              class="margin-left-5"></save-columns-btn>
          </b-col>
        </b-row>
      </div>
    </div>
    <div>
      <b-row>
        <b-col>
          <ag-grid-vue class="ag-theme-balham grid" :gridOptions="gridOptions"
                       :rowData="salesContracts" :columnDefs="columns" @rowDoubleClicked="goToSalesContract"
                       :postProcessPopup="postProcessPopup">
          </ag-grid-vue>
        </b-col>
      </b-row>
      <b-row class="pagination__wrapper" v-if="paginationState">
        <b-col cols="5">
          <div class="pagination-info">{{countOfViewSalesContracts}} {{$t('common_content.of')}} {{salesContractsCount}}
          </div>
        </b-col>
        <b-col cols="7">
          <b-pagination size="md" :total-rows="salesContractsCount" :per-page="searchObject.limit"
                        v-model="currentPaginateIndex"
                        @input="changePaginateState"
          ></b-pagination>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
  import { AgGridVue } from 'ag-grid-vue';
  import { mapGetters, mapState } from 'vuex';
  import Multiselect from 'vue-multiselect';
  import RangeDatePicker from 'vue2-datepicker'
  import moment from 'moment';
  import SaveColumnsBtn from '@/components/common/SaveColumnsBtn';
  import { notificationsRenderer } from '@/helpers/renderHelpers';
  import { gridMixins } from '@/mixins';

  import {
    cleanFilters,
    saveFilters,
    setFilters,
    checkSavedFilterData,
    getArrayByProperty,
    setNotifyData
  } from '@/helpers/commonHelpers';
  import { DATE_FORMAT } from '@/constants';

  export default {
    mixins: [gridMixins],
    components: {
      Multiselect,
      RangeDatePicker,
      'ag-grid-vue': AgGridVue,
      SaveColumnsBtn
    },
    data() {
      return {
        statuses: [{text: 'Draft', key: 'draft'}, {text: 'Active', key: 'active'}, {
          text: 'Completed',
          key: 'completed'
        }],
        searchData: {
          rangeDate: null,
          contract_nr: null,
          statuses: [],
          creators: [],
          buyers: [],
          sellers: [],
          delivery_methods: []
        },
        searchObject: {
          limit: 100,
          offset: 0
        },
        paginationState: true,
        currentPaginateIndex: 1
      }
    },
    methods: {
      search() {
        this.currentPaginateIndex = 1;
        this.searchObject = {
          ...this.searchObject,
          date_end_between_after: this.searchData.rangeDate && this.searchData.rangeDate[0] ? moment(this.searchData.rangeDate[0]).format(DATE_FORMAT) : '',
          date_end_between_before: this.searchData.rangeDate && this.searchData.rangeDate[1] ? moment(this.searchData.rangeDate[1]).format(DATE_FORMAT) : '',
          buyers: getArrayByProperty(this.searchData.buyers, 'id').toString(),
          sellers: getArrayByProperty(this.searchData.sellers, 'id').toString(),
          statuses: getArrayByProperty(this.searchData.statuses, 'key').toString(),
          creators: getArrayByProperty(this.searchData.creators, 'id').toString(),
          contract_nr: this.searchData.contract_nr,
          delivery_methods: getArrayByProperty(this.searchData.delivery_methods, 'id').toString()
        };

        this.$store.dispatch('salesContracts/loadSalesContracts', this.searchObject);
        saveFilters('salesContracts', this.searchData);
      },
      create() {
        this.$router.push({path: `/sales-contracts/new`});
      },
      deleteSalesContracts() {
        const ids = getArrayByProperty(this.gridOptions.api.getSelectedRows(), 'id');
        if (ids.length) {
          this.$http.delete(`sales/sales_contracts/`, {body: {ids}}).then((response) => {
            this.$notify(setNotifyData('Success', response.body, 'success'));
          }).then(() => {
            this.currentPaginateIndex = 1;
            this.$store.dispatch('salesContracts/loadSalesContracts', this.searchObject);
          }).catch((error) => {
            console.log(error);
            notificationsRenderer(error.body, 'error');
          });
        } else {
          this.$notify(setNotifyData('Select sales contracts for removing', '', 'warn'));
        }
      },
      cleanFilters() {
        cleanFilters(this.searchData);
      },
      setQuickFilter(e) {
        this.gridOptions.api.setQuickFilter(e.target.value);
      },
      changePaginateState() {
        const searchObject = {...this.searchObject, offset: (this.currentPaginateIndex - 1) * this.searchObject.limit};
        this.$store.dispatch('salesContracts/loadSalesContracts', searchObject);
      },
      togglePagination() {
        if (!this.paginationState) {
          delete this.searchObject.limit;
          delete this.searchObject.offset;
        } else {
          this.searchObject.limit = 100;
          this.searchObject.offset = 0;
        }
        this.search();
      },
      goToSalesContract(params) {
        if (params.data.id) {
          this.$router.push({path: `/sales-contracts/${params.data.id}`})
        }
      }
    },
    computed: {
      ...mapGetters('partners', {
        buyers: 'customers',
        sellers: 'sellers'
      }),
      ...mapGetters('woodware', {
        delivery_methods: 'delivery_methods',
        creators: 'users'
      }),
      ...mapState('salesContracts', ['gridName']),
      ...mapState('generic', ['currentRoutePermissions']),
      ...mapGetters('salesContracts', {
        gridOptions: 'gridOptions',
        salesContracts: 'salesContracts',
        columns: 'columns',
        salesContractsCount: 'salesContractsCount'
      }),
      countOfViewSalesContracts() {
        return this.salesContracts.length;
      }
    },
    beforeMount() {
      this.$store.dispatch('partners/getCustomers');
      this.$store.dispatch('partners/getSellers');
      this.$store.cache.dispatch('woodware/getDeliveryMethods');
      this.$store.cache.dispatch('woodware/getUsers');
      if (checkSavedFilterData('salesContracts')) {
        this.searchData = setFilters('salesContracts');
      }
      this.search();
    },
    beforeDestroy() {
      const emptyData = {
        data: {
          results: [],
          count: null
        },
        used_columns: [],
        grid_name: ''
      }
      this.$store.commit('salesContracts/setData', emptyData);
    }
  }
</script>

<style scoped>
  .grid {
    height: calc(100vh - 400px);
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/modules/sales/contracts/SalesContractsList.vue